import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cx } from './cx';

interface AccordionProps {
  title: ReactNode;
  /** Élément affiché avant le titre (icône, code…). */
  leading?: ReactNode;
  /** Élément affiché avant le chevron (pourcentage, pastille…). */
  meta?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

/** Bloc repliable : le chevron tourne et passe en indigo, le contenu glisse. */
export function Accordion({ title, leading, meta, defaultOpen = false, children, className }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div
      className={cx(
        'overflow-hidden rounded-row border bg-white transition-colors duration-200',
        open ? 'border-primary-200' : 'border-line',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3.5 px-4 py-3.5 text-left hover:bg-surface-light focus-visible:bg-surface-light focus-visible:outline-none"
      >
        {leading}
        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug tracking-[-.3px] text-heading">{title}</span>
        {meta}
        <ChevronDown
          size={18}
          aria-hidden
          className={cx(
            'shrink-0 transition-transform duration-[350ms] ease-smooth',
            open ? 'rotate-180 text-primary' : 'text-muted',
          )}
        />
      </button>
      <div
        id={id}
        className={cx(
          // `invisible` une fois fermé : contenu retiré du clavier et des lecteurs d'écran.
          'grid transition-[grid-template-rows,visibility] duration-[400ms] ease-smooth motion-reduce:transition-none',
          open ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-line px-4 pb-4 pt-3 text-[13.5px] leading-relaxed text-body">{children}</div>
        </div>
      </div>
    </div>
  );
}
